import axios from 'axios';
import { REACT_APP_TEAMCAMP_APIURL } from '../global/environment';
import { getAPIErrorReason } from '../helpers/common';
import { captureException } from './logService';
import { dispatchChatMessageError } from './chatMessageUtilService';
import { ChatGroupInterface, MessageDetailsInterface } from '../interfaces/chatMessageInterface';
import { CHAT_GROUP_TYPE } from '../global/constants';
import { rootStore } from '../mobx/rootStore';
import { OPEN_CHAT_MEMORY_LIMIT } from '../mobx/stores/chatMessageStore';
import MessageGroupCacheService, { sortConversations } from './messageGroupCacheService';
import ConversationMessageCacheService from './conversationMessageCacheService';

/**
 * Per-conversation messages data layer (`/discussions/getConversations` and
 * `/discussions/getMessages`): the sidebar loads lean conversation summaries, and each
 * conversation loads cursor-paged messages on demand and refreshes with only what changed.
 */

export interface MessageCursor {
  before: string;
  beforeId: string;
}

export interface GetMessagesResult {
  messages: MessageDetailsInterface[];
  hasMore: boolean;
  nextCursor: MessageCursor | null;
  latestUpdatedTime: string;
}

interface ConversationLastMessage {
  id: string;
  message: string;
  senderId: string;
  createdOn: string;
}

interface ConversationResponse
  extends Omit<
    ChatGroupInterface,
    'messages' | 'lastMessage' | 'lastMessageTime' | 'lastMessageText' | 'hasUnreadMessages'
  > {
  lastMessage: ConversationLastMessage | null;
  lastMessageTime: string | null;
  hasUnreadMessages: boolean;
}

// Requests in flight, by url + params, so callers that accept any in-flight response (`shared`)
// join it instead of sending the same request again (e.g. page load and startup sync together).
// Non-shared callers (realtime/own-change refreshes) always send a request that starts after the
// change, so they never get a response that predates it.
const inFlight = new Map<string, Promise<unknown>>();

const getRequest = <T>(url: string, params: Record<string, string | number> | undefined, shared: boolean) => {
  const key = `${url}?${JSON.stringify(params ?? {})}`;
  const pending = inFlight.get(key);
  if (shared && pending) return pending as Promise<T>;
  const request = axios
    .get(url, { params })
    .then((response) => response?.data as T)
    .finally(() => {
      if (inFlight.get(key) === request) inFlight.delete(key);
    });
  inFlight.set(key, request);
  return request;
};

/** Maps a server conversation summary onto the ChatGroupInterface the sidebar renders. */
const mapConversation = (conversation: ConversationResponse): ChatGroupInterface => ({
  ...conversation,
  messages: [],
  lastMessageText: conversation.lastMessage?.message ?? '',
  lastMessageTime: conversation.lastMessageTime ?? conversation.updatedOn,
  hasUnreadMessages: !!conversation.hasUnreadMessages,
  unreadCount: conversation.unreadCount ?? 0
});

/**
 * @desc Loads the conversation list for the sidebar (no message bodies). Already enriched with
 * last-message preview, unread count and pin state, and sorted server-side.
 */
export const getConversations = async (
  ids?: string[],
  shared = false
): Promise<ChatGroupInterface[] | undefined> => {
  try {
    const data = await getRequest<ConversationResponse[]>(
      `${REACT_APP_TEAMCAMP_APIURL}/discussions/getConversations`,
      ids?.length ? { ids: ids.join(',') } : undefined,
      shared
    );
    return (data || []).map(mapConversation);
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get conversations please try again';
    captureException(error);
    dispatchChatMessageError(error);
    return undefined;
  }
};

/**
 * @desc Loads messages for a single conversation.
 * - Page: newest-first via a `(createdOn, id)` cursor; pass the previous `nextCursor` for older.
 * - Changes: with `updatedAfter`, only messages changed since then (archived included).
 * @param conversationId project id (project chat) or message group id (personal chat)
 * @param type GroupType value: 1 = personal, 2 = project
 */
export const getMessages = async (
  conversationId: string,
  type: number,
  options: { cursor?: MessageCursor | null; limit?: number; updatedAfter?: string; shared?: boolean } = {}
): Promise<GetMessagesResult | undefined> => {
  try {
    const { cursor, limit = 30, updatedAfter, shared = false } = options;
    const params: Record<string, string | number> = { conversationId, type, limit };
    if (cursor?.before) {
      params.before = cursor.before;
      params.beforeId = cursor.beforeId;
    }
    if (updatedAfter) params.updatedAfter = updatedAfter;
    return await getRequest<GetMessagesResult>(`${REACT_APP_TEAMCAMP_APIURL}/discussions/getMessages`, params, shared);
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get messages please try again';
    captureException(error);
    dispatchChatMessageError(error);
    return undefined;
  }
};

// Page size for the open navbar conversation (latest page on open, older pages on scroll-up).
const OPEN_CHAT_PAGE_SIZE = 50;

// Change checks also re-read the last minute before the sync point: a message can be timestamped
// before it's saved (the server sends notifications in between), or by a server whose clock
// differs, so it could land just behind the sync point and never be fetched.
const SYNC_OVERLAP_MS = 60 * 1000;

const overlapSince = (since: string) => new Date(new Date(since).getTime() - SYNC_OVERLAP_MS).toISOString();

const toTime = (message: MessageDetailsInterface) => new Date(message.createdOn).getTime();

/**
 * @desc Applies changed messages (from an `updatedAfter` query) to a loaded, oldest → newest list:
 * archived ones are removed, known ones replaced in place, new ones inserted in time order.
 * A changed message older than the loaded window is skipped unless the full history is loaded —
 * it arrives with its own page, so no gap is created. Returns `existing` itself when nothing
 * differs (the sync overlap re-sends unchanged messages), so the list doesn't re-render.
 */
export const applyMessageChanges = (
  existing: MessageDetailsInterface[],
  changes: MessageDetailsInterface[],
  hasFullHistory: boolean
): MessageDetailsInterface[] => {
  const byId = new Map(existing.map((message) => [message.id, message]));
  const oldestLoadedTime = existing.length > 0 ? toTime(existing[0]) : -Infinity;
  let changed = false;
  let inserted = false;
  changes.forEach((change) => {
    const current = byId.get(change.id);
    if (change.isArchived) {
      if (current) {
        byId.delete(change.id);
        changed = true;
      }
    } else if (current) {
      if (JSON.stringify(current) !== JSON.stringify(change)) {
        byId.set(change.id, change);
        changed = true;
      }
    } else if (hasFullHistory || toTime(change) >= oldestLoadedTime) {
      byId.set(change.id, change);
      changed = true;
      inserted = true;
    }
  });
  if (!changed) return existing;
  const merged = Array.from(byId.values());
  return inserted ? merged.sort((a, b) => toTime(a) - toTime(b)) : merged;
};

interface SyncState {
  cursor: MessageCursor | null;
  hasMore: boolean;
  updatedAt: string | null;
}

/**
 * A view showing one conversation's messages: the navbar chat (personal or project group) or the
 * project Messages tab. Both load, page, refresh and cache the conversation the same way.
 */
interface ConversationView {
  openId: () => string | null | undefined;
  messages: () => MessageDetailsInterface[];
  sync: () => SyncState;
  loadingOlder: () => boolean;
  // Applies only while `id` is still the open conversation.
  setMessages: (id: string, messages: MessageDetailsInterface[], sync: SyncState) => void;
  setLoadingOlder: (loading: boolean) => void;
  loadOlderFailed: () => void;
  setLoadFailed: (id: string) => void;
  // Loaded from memory with no change signalled since it was left: no refresh needed on open.
  isFresh: () => boolean;
  // A response for `id` arrived after it was closed; its remembered copy must re-check on open.
  discarded: (id: string) => void;
}

const chatView: ConversationView = {
  openId: () => rootStore.chatMessageStore.currentChatGroup?.id,
  messages: () => rootStore.chatMessageStore.currentChatGroup?.messages || [],
  sync: () => {
    const store = rootStore.chatMessageStore;
    return { cursor: store.openChatCursor, hasMore: store.openChatHasMore, updatedAt: store.openChatUpdatedAt };
  },
  loadingOlder: () => rootStore.chatMessageStore.openChatLoadingOlder,
  setMessages: (id, messages, sync) => rootStore.chatMessageStore.setCurrentChatGroupMessages(id, messages, sync),
  setLoadingOlder: (loading) => rootStore.chatMessageStore.setOpenChatLoadingOlder(loading),
  loadOlderFailed: () => rootStore.chatMessageStore.noteOpenChatLoadOlderFailure(),
  setLoadFailed: (id) => rootStore.chatMessageStore.setOpenChatLoadFailed(id),
  isFresh: () => rootStore.chatMessageStore.openChatRestoredFresh,
  discarded: (id) => rootStore.chatMessageStore.markOpenChatMemoryDirty([id])
};

const projectView: ConversationView = {
  openId: () => rootStore.projectStore.activeDiscussionProjectId,
  messages: () => rootStore.projectStore.discussionData,
  sync: () => {
    const store = rootStore.projectStore;
    return { cursor: store.discussionCursor, hasMore: store.discussionHasMore, updatedAt: store.discussionUpdatedAt };
  },
  loadingOlder: () => rootStore.projectStore.discussionLoadingOlder,
  setMessages: (id, messages, sync) => rootStore.projectStore.setDiscussionMessages(id, messages, sync),
  setLoadingOlder: (loading) => rootStore.projectStore.setDiscussionLoadingOlder(loading),
  loadOlderFailed: () => rootStore.projectStore.noteDiscussionLoadOlderFailure(),
  // The project page ends its own loading state when the load returns.
  setLoadFailed: () => undefined,
  isFresh: () => false,
  discarded: () => undefined
};

// Saves the open conversation (messages + paging + sync point) to IndexedDB; never blocks the UI.
const persist = (view: ConversationView, id: string) => {
  if (view.openId() !== id) return;
  ConversationMessageCacheService.getInstance()?.setConversation({
    conversationId: id,
    messages: view.messages(),
    ...view.sync()
  });
};

/**
 * Loads the latest page into the view. When older pages are already loaded, they are kept: the
 * latest page replaces only its own time window (picking up edits/deletes there) and unsent
 * optimistic messages are preserved. `replace` discards loaded pages instead (they may be stale).
 * `shared` joins an identical request already in flight.
 */
const loadLatest = async (
  view: ConversationView,
  id: string,
  type: number,
  replace: boolean,
  shared: boolean
): Promise<boolean> => {
  const requestedAt = Date.now();
  const result = await getMessages(id, type, { limit: OPEN_CHAT_PAGE_SIZE, shared });
  if (!result?.messages) {
    view.setLoadFailed(id);
    return false;
  }
  if (view.openId() !== id) {
    view.discarded(id);
    return false;
  }
  const current = view.sync();
  const syncedAt = new Date(result.latestUpdatedTime).getTime();
  // A newer load or refresh already applied (e.g. one sent after the user's own message): this
  // older page would drop what it added.
  if (current.updatedAt && new Date(current.updatedAt).getTime() > syncedAt && view.messages().length) return true;
  const latest = result.messages;
  const existing = !replace ? view.messages() : [];
  const latestIds = new Set(latest.map((message) => message.id));
  // Sent while this request was in flight (local or server clock), so the response can't have it.
  const isNewerThanRequest = (message: MessageDetailsInterface) =>
    toTime(message) > Math.min(requestedAt, syncedAt);
  if (latest.length === 0) {
    const sentMeanwhile = existing.filter(isNewerThanRequest);
    view.setMessages(id, sentMeanwhile, { cursor: null, hasMore: false, updatedAt: result.latestUpdatedTime });
    persist(view, id);
    return true;
  }
  const oldestTime = toTime(latest[0]);
  const newestTime = toTime(latest[latest.length - 1]);
  const older = existing.filter((message) => !latestIds.has(message.id) && toTime(message) <= oldestTime);
  const pending = existing.filter(
    (message) => !latestIds.has(message.id) && (toTime(message) > newestTime || isNewerThanRequest(message))
  );
  const keepOlderPaging = older.length > 0;
  view.setMessages(id, [...older, ...latest, ...pending], {
    cursor: keepOlderPaging ? current.cursor : result.nextCursor,
    hasMore: keepOlderPaging ? current.hasMore : result.hasMore,
    updatedAt: result.latestUpdatedTime
  });
  persist(view, id);
  return true;
};

/**
 * Applies only what changed since the last sync (new, edited, reacted, deleted). Falls back to the
 * latest page when there is no sync point yet or the change set was too large. `shared` joins an
 * identical request already in flight; realtime refreshes leave it off so the request starts after
 * the change. Resolves true when the changes were applied.
 */
const refresh = async (view: ConversationView, id: string, type: number, shared: boolean): Promise<boolean> => {
  const since = view.openId() === id ? view.sync().updatedAt : null;
  if (!since) return loadLatest(view, id, type, false, shared);
  const result = await getMessages(id, type, { updatedAfter: overlapSince(since), shared });
  if (!result) return false;
  // Too many changes to apply: older loaded pages may be stale, so start from a fresh page.
  if (result.hasMore) return loadLatest(view, id, type, true, false);
  if (view.openId() !== id) {
    view.discarded(id);
    return false;
  }
  const current = view.sync();
  // Keep the newest sync point if a slower response finishes after a faster one.
  const updatedAt =
    current.updatedAt && new Date(current.updatedAt).getTime() > new Date(result.latestUpdatedTime).getTime()
      ? current.updatedAt
      : result.latestUpdatedTime;
  // Unchanged messages keep the same array, so the list doesn't re-render.
  const messages = applyMessageChanges(view.messages(), result.messages, !current.hasMore);
  view.setMessages(id, messages, {
    ...current,
    updatedAt
  });
  persist(view, id);
  return true;
};

/**
 * Opens a conversation: in memory and nothing signalled since → no request; in memory → only
 * changes; cached → cached pages instantly, then only changes; otherwise the latest page.
 */
const open = async (view: ConversationView, id: string, type: number) => {
  if (view.openId() !== id) return;
  if (view.messages().length && view.sync().updatedAt) {
    if (!view.isFresh()) await refresh(view, id, type, true);
    return;
  }
  const cached = await ConversationMessageCacheService.getInstance()?.getConversation(id);
  if (cached?.updatedAt && view.openId() === id && !view.messages().length) {
    const syncedUntil = new Date(cached.updatedAt).getTime();
    // Drop anything the server hadn't confirmed at that sync (e.g. an unsent message); real ones
    // come back with the change query below.
    view.setMessages(
      id,
      cached.messages.filter((message) => toTime(message) <= syncedUntil),
      { cursor: cached.cursor, hasMore: cached.hasMore, updatedAt: cached.updatedAt }
    );
    await refresh(view, id, type, true);
    return;
  }
  await loadLatest(view, id, type, false, true);
};

// Loads the next older page (cursor infinite-scroll).
const loadOlder = async (view: ConversationView, type: number) => {
  const id = view.openId();
  const { cursor, hasMore } = view.sync();
  if (!id || !hasMore || !cursor || view.loadingOlder()) return;
  view.setLoadingOlder(true);
  try {
    const result = await getMessages(id, type, { cursor, limit: OPEN_CHAT_PAGE_SIZE });
    if (view.openId() !== id) return;
    if (!result) {
      view.loadOlderFailed();
      return;
    }
    const loaded = view.messages();
    const loadedIds = new Set(loaded.map((message) => message.id));
    const older = result.messages.filter((message) => !loadedIds.has(message.id));
    view.setMessages(id, [...older, ...loaded], {
      cursor: result.nextCursor,
      hasMore: result.hasMore,
      updatedAt: view.sync().updatedAt
    });
    persist(view, id);
    // Nothing new was added, so the list length is unchanged; re-arm the scroll trigger.
    if (older.length === 0 && result.hasMore) view.loadOlderFailed();
  } finally {
    view.setLoadingOlder(false);
  }
};

/**
 * @desc Loads the most recently opened conversations from IndexedDB into memory, so switching to
 * them renders at once (no loader) and then fetches only what changed.
 */
export const preloadOpenChatMemory = async () => {
  const cached = await ConversationMessageCacheService.getInstance()?.getRecentConversations(OPEN_CHAT_MEMORY_LIMIT);
  if (cached?.length) rootStore.chatMessageStore.seedOpenChatMemory(cached);
};

/**
 * @desc Opens a navbar conversation (cache first, then only what changed; else the latest page).
 */
export const openChatGroupMessages = (groupId: string, type?: number) =>
  open(chatView, groupId, type ?? CHAT_GROUP_TYPE.PERSONAL);

/**
 * @desc Refreshes the open navbar conversation with only what changed since the last sync.
 */
export const refreshOpenChatGroupMessages = (groupId: string, type?: number, shared = false) =>
  refresh(chatView, groupId, type ?? CHAT_GROUP_TYPE.PERSONAL, shared);

// Same fallback as the server when a conversation was never seen; keep in sync with
// DEFAULT_LAST_SEEN in teamcamp-backend src/services/discussionServices.tsx.
const DEFAULT_LAST_SEEN = '2025-01-13T11:50:23.196Z';

const lastSeenTimeOf = (row: ChatGroupInterface, userId?: string) => {
  const seen = new Date((userId && row.lastSeen?.[userId]) || DEFAULT_LAST_SEEN).getTime();
  return Number.isNaN(seen) ? new Date(DEFAULT_LAST_SEEN).getTime() : seen;
};

/**
 * @desc Builds the sidebar row of the open chat/project from its freshly synced messages, with the
 * server's rules (last message preview, unread after lastSeen), so a change there needs no
 * `getConversations?ids` request. Undefined when it can't be exact (row unknown, not synced, or
 * unread messages older than the loaded window) — then the server row is used.
 */
export const deriveConversationRow = (id: string): ChatGroupInterface | undefined => {
  const view = chatView.openId() === id ? chatView : projectView.openId() === id ? projectView : undefined;
  const row = rootStore.chatMessageStore.chatGroups.find((group) => group.id === id);
  const { hasMore, updatedAt } = view?.sync() || {};
  if (!view || !row || !updatedAt) return undefined;
  const syncedUntil = new Date(updatedAt).getTime();
  // Unconfirmed (optimistic) messages aren't on the server yet.
  const confirmed = view.messages().filter((message) => toTime(message) <= syncedUntil);
  const newest = confirmed[confirmed.length - 1];
  if (!newest && hasMore) return undefined;
  const userId = rootStore.currentUser?.id;
  const lastSeenTime = lastSeenTimeOf(row, userId);
  if (hasMore && toTime(confirmed[0]) > lastSeenTime) return undefined;
  const unreadCount = confirmed.filter(
    (message) => message.sender?.id !== userId && toTime(message) > lastSeenTime
  ).length;
  const preview = newest
    ? newest.message || (newest as MessageDetailsInterface & { file?: { name?: string } }).file?.name || ''
    : '';
  return {
    ...row,
    messages: [],
    lastMessage: newest
      ? { id: newest.id, message: preview, senderId: newest.sender?.id ?? '', createdOn: newest.createdOn }
      : null,
    lastMessageText: preview,
    lastMessageTime: newest?.createdOn ?? row.updatedOn,
    unreadCount,
    hasUnreadMessages: !!newest && newest.sender?.id !== userId && lastSeenTime < toTime(newest)
  };
};

/**
 * @desc Loads the next older page of the open navbar conversation.
 */
export const loadOlderOpenChatMessages = () =>
  loadOlder(chatView, rootStore.chatMessageStore.currentChatGroup?.type ?? CHAT_GROUP_TYPE.PERSONAL);

/**
 * @desc Opens the project Messages tab: same paging, cache and change sync as the navbar chat
 * (the project's group chat is the same conversation, so they share the cache).
 */
export const loadProjectDiscussion = async (projectId: string): Promise<MessageDetailsInterface[]> => {
  await open(projectView, projectId, CHAT_GROUP_TYPE.PROJECT);
  return rootStore.projectStore.discussionData;
};

/**
 * @desc Refreshes the open project discussion with only what changed since the last sync.
 */
export const refreshProjectDiscussion = (projectId: string) =>
  refresh(projectView, projectId, CHAT_GROUP_TYPE.PROJECT, false);

/**
 * @desc Loads the next older page of the open project discussion.
 */
export const loadOlderProjectMessages = () => loadOlder(projectView, CHAT_GROUP_TYPE.PROJECT);

/**
 * @desc Replaces only the changed sidebar rows with fresh server summaries and re-sorts. A changed
 * id the server no longer returns (left the group, project archived) is removed.
 */
export const mergeConversationRows = async (changedIds: string[], rows: ChatGroupInterface[]) => {
  const store = rootStore.chatMessageStore;
  const changed = new Set(changedIds);
  const merged = sortConversations([...store.chatGroups.filter((group) => !changed.has(group.id)), ...rows]);
  store.setChatGroups(merged);
  // Cache keeps groups (with members) for lookups such as resolving an existing DM.
  await MessageGroupCacheService.getInstance()?.setMessageGroups(merged);
};

interface RecentMessagesResponse {
  conversations: Record<string, { messages: MessageDetailsInterface[]; hasMore: boolean; nextCursor: MessageCursor | null }>;
  latestUpdatedTime: string;
}

const PREFETCH_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;
const PREFETCH_MAX_CONVERSATIONS = 35;
const prefetchedWorkspaces = new Set<string>();

const toTimestamp = (value?: string | null) => (value ? new Date(value).getTime() : 0);

/**
 * @desc First load only (nothing cached): loads the latest page of recently active conversations in
 * one request into IndexedDB and memory. Once per workspace per session.
 */
export const prefetchRecentConversations = async () => {
  try {
    const workspaceId = rootStore.workspaceStore.currentWorkspace?.id;
    const cache = ConversationMessageCacheService.getInstance();
    if (!workspaceId || !cache || prefetchedWorkspaces.has(workspaceId)) return;
    prefetchedWorkspaces.add(workspaceId);

    const store = rootStore.chatMessageStore;
    // The open chat doesn't count: /messages opens and caches one before this runs.
    const openId = store.currentChatGroup?.id;
    if (store.openChatMemory.size > 0 || (await cache.hasCachedConversations(openId))) return;
    const since = Date.now() - PREFETCH_WINDOW_MS;
    const recent = store.chatGroups
      .filter((group) => group.lastMessage && toTimestamp(group.lastMessageTime) >= since)
      .sort((a, b) => toTimestamp(b.lastMessageTime) - toTimestamp(a.lastMessageTime))
      .slice(0, PREFETCH_MAX_CONVERSATIONS);
    const targets = recent.filter((group) => group.id !== store.currentChatGroup?.id);
    if (targets.length === 0) return;

    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/discussions/getRecentMessages`, {
      conversations: targets.map((group) => ({ id: group.id, type: group.type ?? CHAT_GROUP_TYPE.PERSONAL })),
      limit: OPEN_CHAT_PAGE_SIZE
    });
    const { conversations, latestUpdatedTime } = (response?.data || {}) as RecentMessagesResponse;
    if (!conversations) return;

    // Oldest first: memory evicts the earliest added, and IndexedDB's access order (used to restore
    // memory on reload) then matches the sidebar.
    const loaded = targets
      .slice()
      .reverse()
      .filter((group) => conversations[group.id] && group.id !== store.currentChatGroup?.id)
      .map((group) => {
        const page = conversations[group.id];
        return {
          conversationId: group.id,
          messages: page.messages,
          cursor: page.nextCursor,
          hasMore: page.hasMore,
          updatedAt: latestUpdatedTime
        };
      });
    store.seedOpenChatMemory(loaded);
    for (const conversation of loaded) await cache.setConversation(conversation);
  } catch (e) {
    captureException(e);
  }
};

// Runs when the browser is idle so it doesn't compete with the first render.
export const scheduleRecentConversationsPrefetch = () => {
  const run = () => {
    prefetchRecentConversations();
  };
  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    window.requestIdleCallback(run, { timeout: 5000 });
  } else {
    setTimeout(run, 2000);
  }
};

// New message carried in the realtime signal (backend `MessageSignalLastInterface`). It has no
// message text, so the row's preview text stays empty until the exact row is fetched.
export interface SignalLastMessage {
  id: string;
  senderId: string;
  createdOn: string;
}

/**
 * @desc Updates a sidebar row from the new message in a realtime signal (preview, time, unread),
 * so no request is needed right away. The unread count is bumped here and made exact by a later
 * batched row fetch. Undefined when the row is unknown or already shows a newer message.
 */
export const applySignalPreview = (id: string, last: SignalLastMessage): ChatGroupInterface | undefined => {
  const row = rootStore.chatMessageStore.chatGroups.find((group) => group.id === id);
  const createdAt = new Date(last?.createdOn).getTime();
  if (!row || Number.isNaN(createdAt) || createdAt <= toTimestamp(row.lastMessageTime)) return undefined;
  const userId = rootStore.currentUser?.id;
  const unread = last.senderId !== userId && createdAt > lastSeenTimeOf(row, userId);
  return {
    ...row,
    messages: [],
    lastMessage: { id: last.id, message: '', senderId: last.senderId, createdOn: last.createdOn },
    lastMessageText: '',
    lastMessageTime: last.createdOn,
    hasUnreadMessages: unread,
    unreadCount: unread ? (row.unreadCount ?? 0) + 1 : row.unreadCount ?? 0
  };
};
